import { PaginatedDataDTO } from '@bottega52/commons-pagination';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { DeleteOutline, SearchOutlined } from '@material-ui/icons';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { Button } from '@mui/material';
import { Stack } from '@mui/system';
import { DataGrid, GridColumns, GridRenderCellParams } from '@mui/x-data-grid';
import * as React from "react";
import { ICustomerContractLegalDocumentInDTO } from '../../repository/jago/model/input/ICustomerContractLegalDocumentInDTO';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { PERMISSIONS } from '../../services/PermissionService/PermissionConstants';
import translations from '../../translations/i18next';
import ReduxLanguage from '../ReduxLanguage';

const styles = createStyles({
  rowContainer: {
    display: 'flex',
    margin: 10,
    flexDirection: 'column'
  },
  rowBodyContainer: {
    padding: 15,
    minHeight: 100,
  },
  rowTitle: {
    fontWeight: 'bold'
  },
  logo: {
    width: '20%',
  },
});


export interface ICustomerContractDocumentsTableState {
}

export interface ICustomerContractDocumentsTableProps extends WithStyles<typeof styles>{
  customerDocuments: PaginatedDataDTO<ICustomerContractLegalDocumentInDTO>;
  tableColumns?: GridColumns<ICustomerContractLegalDocumentInDTO>;
  onRowClick?: (customerDocument: ICustomerContractLegalDocumentInDTO) => void;
  onFetchElementsOnPage: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onDeleteContractDocumentRequest?: (domain: ICustomerContractLegalDocumentInDTO) => void;
  onDownloadContractDocumentRequest?: (domain: ICustomerContractLegalDocumentInDTO) => void;
  rowsPerPage: number;
  rowHeight?: number;
  disableSelectionOnClick?: boolean;
  isLoading?: boolean;
}

class CustomerContractDocumentsTable extends React.Component<ICustomerContractDocumentsTableProps, ICustomerContractDocumentsTableState> {
  private defaultColumns: GridColumns<ICustomerContractLegalDocumentInDTO>;
  public render() {
    const { customerDocuments, disableSelectionOnClick, onRowClick, tableColumns, rowHeight, onDeleteContractDocumentRequest, onDownloadContractDocumentRequest } = this.props;
    this.defaultColumns = [
      {
        field: 'name',
        headerName: translations.t('gateways.name'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 350,
        flex: 1,
        renderCell: (params: GridRenderCellParams<any>) => {
          const legalDocument = (params.row || {});
          return (
            <div>
              <h3 style={{ margin: 0,  fontWeight: 'bold' }}>{(legalDocument.fileName || "").replace("customer-contracts/","")}</h3>
            </div>
        )}
      },
      ...(
        AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CONTRACTS_READ) ? [{
          field: 'downloadDocument',
          headerName: translations.t('credits.downloadDocument'),
          renderCell: (params: any) => {
            return (
              <Button
                variant="contained"
                style={{
                  backgroundColor: 'darkcyan',
                  flexShrink: 0,
                  marginRight: 20,
                  width: 'fit-content',
                  color: 'white',
                  padding: 4,
                  paddingLeft: 10,
                  paddingRight: 10,
                  fontWeight: 'bold'
                }}
                onClick={(e) => {e.stopPropagation(); onDownloadContractDocumentRequest(params.row)}}
              >
                <CloudDownloadIcon style={{ fontSize: 15 }}/>
                <h5 style={{ fontWeight: 'bold', margin: 0, marginLeft: 5 }} ><ReduxLanguage languageKey={"credits.downloadDocument"} /></h5>
              </Button>
            )
          },
          type: 'string',
          sortable: false,
          minWidth: 200,
        }] : []
      ),
      ...(
        AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CUSTOMER_PLANTS_DELETE) ? [{
          field: 'actions',
          headerName: translations.t('forms.actions'),
          renderCell: (params: any) => {
            return (
              <Button
                variant="contained"
                style={{
                  backgroundColor: 'darkred',
                  flexShrink: 0,
                  marginRight: 20,
                  width: 'fit-content',
                  color: 'white',
                  padding: 4,
                  paddingLeft: 10,
                  paddingRight: 10,
                  fontWeight: 'bold'
                }}
                onClick={(e) => {e.stopPropagation(); onDeleteContractDocumentRequest(params.row)}}
              >
                <DeleteOutline style={{ fontSize: 15 }}/>
                <h5 style={{ fontWeight: 'bold', margin: 0, marginLeft: 5 }} ><ReduxLanguage languageKey={"forms.delete"} /></h5>
              </Button>
            )
          },
          type: 'string',
          sortable: false,
          minWidth: 200,
        }] : []
      ),
    ];

    return (
      <DataGrid
        getRowId={(d) => d.fileName}
        rows={customerDocuments.content || []}
        columns={tableColumns ? tableColumns: this.defaultColumns}
        rowHeight={rowHeight ? rowHeight: 55}
        onPageSizeChange={null}
        onPageChange={null}
        page={null}
        rowCount={null}
        pageSize={null}
        loading={this.props.isLoading}
        onRowClick={row => onRowClick ? onRowClick(row.row) : null}
        disableSelectionOnClick={disableSelectionOnClick}
        hideFooter={true}
        componentsProps={{
          pagination: {
            labelRowsPerPage: translations.t('forms.rowsPerPage')
          }
        }}
        components={{
          NoRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                <SearchOutlined style={{ alignSelf: 'center' }} />
                <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}><ReduxLanguage languageKey='credits.noCustomerContractDocumentFindTitle' /></h3>
                <h4 style={{ textAlign: 'center', marginTop: 0 }}><ReduxLanguage languageKey='credits.noCustomerContractDocumentFindDescription' /></h4>
              </div>
            </Stack>
          ),
          NoResultsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}><ReduxLanguage languageKey='credits.noCustomerContractDocumentFindTitle' /></h3>
              <h4 style={{ textAlign: 'center', marginTop: 0 }}><ReduxLanguage languageKey='credits.noCustomerContractDocumentFindDescription' /></h4>
            </Stack>
          )
        }}
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
      />
    );
  }
}

export default withStyles(styles)(CustomerContractDocumentsTable);